import { Cancel, History, Refresh } from '@mui/icons-material';
import { Grid, Box, Button, IconButton, Typography, Theme, useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useContext, useState } from 'react';
import { uploadLenderLetter, uploadDeposit } from '../../../fetch';
import { FileDrop } from '../../../components';
import {
  ISpec,
  IContractDocumentResults,
  BuildDocumentType,
  IContractDocument
} from '../../../models';
import { useSnackbar } from 'notistack';
import { UserContext } from '../../../context';
import { useDropzone } from 'react-dropzone';
import { FileHistoryModal } from '../../sales/under-contract/under-contract-details/contract-files/file-history-modal';
import { allImageFileTypes } from '../../../components/file/utils';

interface IContractDocumentsUploads {
  spec: ISpec;
  contractDocumentResults: IContractDocumentResults;
  refetchContractDocumentResults: any;
}

const depositDropzoneFileTypes = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/tiff': ['.tiff'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf']
};

const lenderLetterDropzoneFileTypes = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/tiff': ['.tiff'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf']
};
export const ContractDocumentsUploads: FC<IContractDocumentsUploads> = ({
  spec,
  contractDocumentResults,
  refetchContractDocumentResults
}) => {
  const { user } = useContext(UserContext);
  const isClient = user?.userRoles?.includes('BL_Client');
  const isMobile = useMediaQuery('(max-width: 960px)');
  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();

  const lenderLetterDocument = contractDocumentResults?.contractDocuments?.find(
    doc => doc.buildDocumentTypeId === BuildDocumentType.LenderLetter
  );
  const depositDocument = contractDocumentResults?.contractDocuments?.find(
    doc => doc.buildDocumentTypeId === BuildDocumentType.AddendumE
  );
  const [selectedBuildDocument, setSelectedBuildDocument] = useState<IContractDocument | null>(
    null
  );

  const [isReplacingLenderLetter, setIsReplacingLenderLetter] = useState<boolean>(false);
  const handleLenderLetterSave = async (finalContractData: any) => {
    try {
      setIsReplacingLenderLetter(true);

      const formData = new FormData();
      formData.append('LenderLetter', finalContractData);

      setIsShowingLenderLetterDropzone(false);
      await uploadLenderLetter(spec.buildId, formData);
      await refetchContractDocumentResults();
      setIsReplacingLenderLetter(false);
      enqueueSnackbar('Success, lender letter uploaded!', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error uploading lender letter, please try again.`, {
        variant: 'error'
      });
      setIsReplacingLenderLetter(false);
      console.log(error);
    }
  };

  const [isReplacingDeposit, setIsReplacingDeposit] = useState<boolean>(false);
  const handleDepositSave = async (finalContractData: any) => {
    try {
      setIsReplacingDeposit(true);

      const formData = new FormData();
      formData.append('Deposit', finalContractData);

      setIsShowingDepositDropzone(false);
      await uploadDeposit(spec.buildId, formData);
      await refetchContractDocumentResults();
      setIsReplacingDeposit(false);
      enqueueSnackbar('Success, deposit document uploaded!', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error uploading deposit document, please try again.`, {
        variant: 'error'
      });
      setIsReplacingDeposit(false);
      console.log(error);
    }
  };

  const [isShowingLenderLetterDropzone, setIsShowingLenderLetterDropzone] = useState(false);
  const [isShowingDepositDropzone, setIsShowingDepositDropzone] = useState(false);

  const uploadLenderLetterDZProps = useDropzone({
    accept: lenderLetterDropzoneFileTypes,
    useFsAccessApi: false,
    onDrop: acceptedFiles => {
      if (acceptedFiles.length > 0) {
        const acceptedFile = acceptedFiles[0];
        handleLenderLetterSave(acceptedFile);
      } else {
        enqueueSnackbar(
          `Invalid file type. Please upload one of these image types, ${allImageFileTypes.join(
            ', '
          )}.`,
          {
            variant: 'error'
          }
        );
      }
    }
  });

  const uploadDepositDZProps = useDropzone({
    accept: depositDropzoneFileTypes,
    useFsAccessApi: false,
    onDrop: acceptedFiles => {
      if (acceptedFiles.length > 0) {
        const acceptedFile = acceptedFiles[0];
        handleDepositSave(acceptedFile);
      } else {
        enqueueSnackbar(
          `Invalid file type. Please upload one of these image types, ${allImageFileTypes.join(
            ', '
          )}.`,
          {
            variant: 'error'
          }
        );
      }
    }
  });

  const handleFileHistoryClose = () => {
    setSelectedBuildDocument(null);
  };

  return (
    <>
      <Grid container item xs={12} spacing={1}>
        {!isClient ? (
          <Grid item xs={isMobile ? 12 : 6} display={'flex'} justifyContent={'center'}>
            {isShowingDepositDropzone ? (
              <Box className={classes.dragBox}>
                <IconButton
                  aria-label="hide dropzone"
                  onClick={() => setIsShowingDepositDropzone(false)}
                >
                  <Cancel />
                </IconButton>
                <FileDrop id="File-drop-deposit" {...uploadDepositDZProps} title="">
                  <Typography variant="body1" className={classes.dragAndDrop}>
                    Drag and drop a file or{' '}
                    <Box
                      component="a"
                      href="/#"
                      sx={{ color: theme => theme.palette.primary.main, textDecoration: 'none' }}
                      onClick={e => e.preventDefault()}
                    >
                      browse and pick
                    </Box>{' '}
                  </Typography>
                </FileDrop>
              </Box>
            ) : (
              <Button
                disabled={isClient || isReplacingDeposit}
                onClick={() => setIsShowingDepositDropzone(true)}
                variant="contained"
                color="primary"
                className={classes.button}
                startIcon={<Refresh />}
              >
                {isReplacingDeposit ? 'Replacing...' : 'Deposit'}
              </Button>
            )}
            <Button
              sx={theme => ({ marginLeft: theme.spacing(1) })}
              onClick={() => setSelectedBuildDocument(depositDocument)}
              variant="text"
              color="primary"
              disabled={!depositDocument}
              startIcon={<History />}
            >
              History
            </Button>
          </Grid>
        ) : null}
        {!isClient ? (
          <Grid item xs={isMobile ? 12 : 6} display={'flex'} justifyContent={'center'}>
            {isShowingLenderLetterDropzone ? (
              <Box className={classes.dragBox}>
                <IconButton
                  aria-label="hide dropzone"
                  onClick={() => setIsShowingLenderLetterDropzone(false)}
                >
                  <Cancel />
                </IconButton>
                <FileDrop id="File-drop-lender-letter" {...uploadLenderLetterDZProps} title="">
                  <Typography variant="body1" className={classes.dragAndDrop}>
                    Drag and drop a file or{' '}
                    <Box
                      component="a"
                      href="/#"
                      sx={{ color: theme => theme.palette.primary.main, textDecoration: 'none' }}
                      onClick={e => e.preventDefault()}
                    >
                      browse and pick
                    </Box>{' '}
                  </Typography>
                </FileDrop>
              </Box>
            ) : (
              <Button
                disabled={isClient || isReplacingLenderLetter}
                onClick={() => setIsShowingLenderLetterDropzone(true)}
                variant="contained"
                color="primary"
                className={classes.button}
              >
                {isReplacingLenderLetter ? 'Replacing...' : 'Lender Letter'}
              </Button>
            )}
            <Button
              sx={theme => ({ marginLeft: theme.spacing(1) })}
              onClick={() => setSelectedBuildDocument(lenderLetterDocument)}
              variant="text"
              color="primary"
              disabled={!lenderLetterDocument}
            >
              History
            </Button>
          </Grid>
        ) : null}
      </Grid>
      <FileHistoryModal
        documentName={selectedBuildDocument?.name}
        buildDocumentId={selectedBuildDocument?.buildDocumentId}
        onClose={handleFileHistoryClose}
      />
    </>
  );
};

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  button: {
    width: '152px',
    height: '42px'
  },
  dragAndDrop: {
    marginTop: '8px !important',
    marginBottom: '12px !important'
  },
  dragBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    height: '42px',
    boxSizing: 'border-box',
    [theme.breakpoints.down('md')]: {
      height: '74px'
    }
  }
}));
